import { Trans } from '@lingui/macro'
import { Grid, Typography } from '@mui/material'
import { getLabelFromTranslationData } from 'app/views/common/translationsCommon'
import { FormElementTitle } from 'app/views/forms/common/FormElementTitle'
import { isArray } from 'lodash'
import { myI18n } from 'translation/I18nConnectedProvider'
import {
    geoData,
    scopeOptions
} from './FormCensusDivision'

export const getValuesForCensusDivisionPrint = (value, langVersion) => {
  value = value || {}
  /** convert provinces in selectedScope to readable format */
  const provinces = isArray(value.selectedScope)
    ? value.selectedScope
      .filter((item) => item.includes('_province'))
      .map((scope, index, array) => (
        <>
          {getLabelFromTranslationData({
            data: geoData[scope]?.label,
            langVersion
          })}
          {index < array.length - 1 ? ', ' : ''}
        </>
      )
      )
    : ''

  /** convert other geo objects in selectedScope to readable format */
  const selected = isArray(value.selectedScope)
    ? value.selectedScope
      .filter((item) => !item.includes('_province'))
      .map((scope, index, array) => (
        <>
          {getLabelFromTranslationData({
            data: geoData[scope]?.label,
            langVersion
          })}
          {index < array.length - 1 ? ', ' : ''}
        </>
      ))
    : getLabelFromTranslationData({
      data: geoData[value.selectedScope]?.label,
      langVersion
    })

  /** convert value to object to pass to CensusDivisionPrint component */
  return {
    scopeType: myI18n._(
      scopeOptions.find((item) => item.value === value.scopeType)?.label
    ),
    muncipality: ['Neighbourhood', 'Municipal'].includes(value.scopeType)
      ? getLabelFromTranslationData({
        data: geoData[value.selectedScope]?.label,
        langVersion
      })
      : '',
    provinces: value.pilotPhase ? '' : provinces.length > 0 ? provinces : '',
    selected: value.pilotPhase ? '' : selected,
    pilotPhase: value.pilotPhase ? value.pilotPhase : '',
    pilotPhaseScopeType: value.pilotPhaseScopeType
      ? myI18n._(
        scopeOptions.find((item) => item.value === value.pilotPhaseScopeType)
          ?.label
      )
      : '',
    pilotPhaseMuncipality: ['Neighbourhood', 'Municipal'].includes(
      value.pilotPhaseScopeType
    )
      ? getLabelFromTranslationData({
        data: geoData[value.pilotPhaseScopeType]?.label,
        langVersion
      })
      : '',
    pilotPhaseProvinces: value.pilotPhase
      ? provinces.length > 0
        ? provinces
        : ''
      : '',
    pilotPhaseSelected: value.pilotPhase ? selected : ''
  }
}

/**
 * Component to render FormCenzusDivision element in print preview
 * @param {object} value - value of FormCenzusDivision element
 * @param {string} title - title of FormCenzusDivision element
 * @param {object} props - props of FormCenzusDivision element
 */
export const FormCensusDivisionPrint = ({
  value,
  title,
  langVersion,
  ...props
}) => {
  value = value || {}

  const census = getValuesForCensusDivisionPrint(value, langVersion)

  /** helper function to transform array element to element to display */
  const getTransformedElements = (array) =>
    isArray(array)
      ? array.map((item, index) => (
        <Grid item key={index}>
          {item}
        </Grid>
      ))
      : ''

  census.provinces = getTransformedElements(census.provinces)
  census.selected = getTransformedElements(census.selected)
  census.pilotPhaseProvinces = getTransformedElements(
    census.pilotPhaseProvinces
  )
  census.pilotPhaseSelected = getTransformedElements(census.pilotPhaseSelected)

  return (
    <>
      {title && <Typography className='form-read-only-header'>{title}</Typography>}
      <CensusDivisionPrint census={census} />
    </>
  )
}

// /**
//  * Helper component to render FormCenzusDivision element in print mode
//  * and readabale format in other places
//  * @param {object} census - converted value from FormCenzusDivision element
//  */
export const CensusDivisionPrint = ({ census }) => {
  const { scopeType } = census

  const municipalDisplay = ['Neighbourhood', 'Municipal'].includes(
    scopeType
  )
  const regionalDisplay = ['Regional', 'Provincial or territorial'].includes(
    scopeType
  )

  return (
    <Grid>
      <Grid item xs style={{ padding: 4 }}>
        <FormElementTitle
          title={<Trans>CENZUS_DIVISION_ELEMENT_SCOPE</Trans>}
        />
        <Typography className='form-read-only-text' style={{ whiteSpace: 'pre-line' }}>
          {census.scopeType}
        </Typography>
      </Grid>
      {census.muncipality && (
        <Grid item xs style={{ padding: 4 }}>
          <FormElementTitle
            title={<Trans>CENZUS_DIVISION_ELEMENT_WHICH_MUNCIPALITY</Trans>}
          />
          <Typography className='form-read-only-text' style={{ whiteSpace: 'pre-line' }}>
            {census.muncipality}
          </Typography>
        </Grid>
      )}
      {census.provinces && (
        <Grid item xs style={{ padding: 4 }}>
          <FormElementTitle
            title={<Trans>CENZUS_DIVISION_ELEMENT_PROVINCES</Trans>}
          />
          <Typography className='form-read-only-text' style={{ whiteSpace: 'pre-line' }}>
            {census.provinces}
          </Typography>
        </Grid>
      )}
      {(municipalDisplay || regionalDisplay) &&
        (
          <Grid item xs style={{ padding: 4 }}>
            <FormElementTitle
              title={<Trans>CENZUS_DIVISION_ELEMENT_SELECTED_LABEL</Trans>}
            />
            <Typography className='form-read-only-text' style={{ whiteSpace: 'pre-line' }}>
              {census.selected}
            </Typography>
          </Grid>
        )}
      {census.pilotPhase && (
        <Grid item xs style={{ padding: 4 }}>
          <FormElementTitle
            title={<Trans>CENZUS_DIVISION_ELEMENT_PILOT_PHASE_QUESTION</Trans>}
          />
          <Typography className='form-read-only-text' style={{ whiteSpace: 'pre-line' }}>
            {census.pilotPhase}
          </Typography>
        </Grid>
      )}
      {census.pilotPhaseScopeType && (
        <Grid item xs style={{ padding: 4 }}>
          <FormElementTitle
            title={<Trans>CENZUS_DIVISION_ELEMENT_SELECT_PILOT_PHASE_SCOPE</Trans>}
          />
          <Typography className='form-read-only-text' style={{ whiteSpace: 'pre-line' }}>
            {census.pilotPhaseScopeType}
          </Typography>
        </Grid>
      )}
      {census.pilotPhaseMuncipality && (
        <Grid item xs style={{ padding: 4 }}>
          <FormElementTitle
            title={<Trans>
              CENZUS_DIVISION_ELEMENT_WHICH_MUNCIPALITY_PILOT_PHASE
            </Trans>}
          />
          <Typography className='form-read-only-text' style={{ whiteSpace: 'pre-line' }}>
            {census.pilotPhaseMuncipality}
          </Typography>
        </Grid>
      )}
      {census.pilotPhaseProvinces && (
        <Grid item xs style={{ padding: 4 }}>
          <FormElementTitle
            title={<Trans>CENZUS_DIVISION_ELEMENT_PROVINCES_PILOT_PHASE</Trans>}
          />
          <Typography className='form-read-only-text' style={{ whiteSpace: 'pre-line' }}>
            {census.pilotPhaseProvinces}
          </Typography>
        </Grid>
      )}
      {census.pilotPhaseSelected && (Array.isArray(census.pilotPhaseSelected) && census.pilotPhaseSelected.length > 0) && (
        <Grid item xs style={{ padding: 4 }}>
          <FormElementTitle
            title={<Trans>CENZUS_DIVISION_ELEMENT_PILOT_PHASE_SELECTED_LABEL</Trans>}
          />
          <Typography className='form-read-only-text' style={{ whiteSpace: 'pre-line' }}>
            {typeof census.pilotPhaseSelected === 'string'
              ? (
                <Trans>{census.pilotPhaseSelected}</Trans>
                )
              : (
                  census.pilotPhaseSelected
                )}
          </Typography>
        </Grid>
      )}
    </Grid>

  )
}
